import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './ui.css';

interface PaginationProps {
  pageNumber: number;
  /** Total pages. When 0 (truly unknown), numbered buttons are omitted. */
  totalPages: number;
  /** Total record count. When 0 but `totalPages` > 0, "Showing X–Y" is shown
   *  without the "of Z" suffix. When both are 0, "Page N" is shown. */
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  /** Records per page — used to compute the "Showing X–Y" range. */
  pageSize?: number;
  onChange: (pageNumber: number) => void;
}

/**
 * Builds a compact page list with ellipsis, mirroring the AdminLTE v4 layout:
 * « 1 2 3 … 5 ». At most 5 visible page numbers; when the total is small every
 * page is shown directly. When `totalPages` is 0 (unknown), only ‹ › is shown.
 */
const pageRange = (current: number, total: number): (number | '…')[] => {
  if (total <= 0) return [];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '…')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');

  pages.push(total);
  return pages;
};

export function Pagination({
  pageNumber,
  totalPages,
  totalCount,
  hasPreviousPage,
  hasNextPage,
  pageSize = 20,
  onChange,
}: PaginationProps) {
  const { t } = useTranslation('admin');

  const hasTotalCount = totalCount > 0;
  const hasPages = totalPages > 0;
  const from = (pageNumber - 1) * pageSize + 1;
  const to = hasTotalCount
    ? Math.min(pageNumber * pageSize, totalCount)
    : (pageNumber - 1) * pageSize + pageSize;
  const pages = pageRange(pageNumber, Math.max(totalPages, 0));

  const label = hasTotalCount
    ? t('common.showing', { from, to, total: totalCount })
    : hasPages
      ? t('common.showingRange', { from, to })
      : t('common.pageNumber', { page: pageNumber });

  return (
    <nav className="ui-pagination" aria-label={t('common.paginationLabel')}>
      <span>{label}</span>
      <div className="ui-pagination__controls">
        <button
          type="button"
          className="ui-pagination__page"
          disabled={!hasPreviousPage}
          onClick={() => onChange(1)}
          aria-label={t('common.firstPage')}>
          «
        </button>
        <button
          type="button"
          className="ui-pagination__page"
          disabled={!hasPreviousPage}
          onClick={() => onChange(pageNumber - 1)}
          aria-label={t('common.previousPage')}>
          ‹
        </button>
        {pages.map((page, index) =>
          page === '…' ? (
            <span key={`ellipsis-${index}`} className="ui-pagination__ellipsis">
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              className={
                page === pageNumber
                  ? 'ui-pagination__page ui-pagination__page--active'
                  : 'ui-pagination__page'
              }
              aria-current={page === pageNumber ? 'page' : undefined}
              aria-label={t('common.pageNumber', { page })}
              onClick={() => onChange(page)}>
              {page}
            </button>
          ),
        )}
        <button
          type="button"
          className="ui-pagination__page"
          disabled={!hasNextPage}
          onClick={() => onChange(pageNumber + 1)}
          aria-label={t('common.nextPage')}>
          ›
        </button>
        {hasPages ? (
          <button
            type="button"
            className="ui-pagination__page"
            disabled={!hasNextPage}
            onClick={() => onChange(totalPages)}
            aria-label={t('common.lastPage')}>
            »
          </button>
        ) : null}
      </div>
    </nav>
  );
}
